import { lazy } from 'react';

/**
 * Wrap `React.lazy` with stale-chunk recovery.
 *
 * Problem: after we deploy, the new build's chunks have new hashed
 * filenames (e.g. `Progress-9IEkpwNU.js` becomes `Progress-aBcD1234.js`).
 * If a member has the old app loaded and navigates to a lazy-loaded
 * route, the dynamic `import()` requests the OLD filename, which 404s.
 * Firebase Hosting then serves `index.html` as the fallback, the
 * browser sees an HTML payload with content-type `text/html`, and the
 * module-loader throws `TypeError: 'text/html' is not a valid
 * JavaScript MIME type` (or `Failed to fetch dynamically imported
 * module`). User's session becomes unusable until they manually
 * reload — but most people don't know to reload.
 *
 * Fix: on import failure, set a session flag and force a hard reload.
 * The reload picks up the latest build, the user's URL is preserved,
 * the session flag prevents infinite reload loops if the failure is
 * NOT a stale chunk (e.g. genuine network failure during initial
 * page load — we should surface that rather than spinning).
 *
 * Detection heuristic: any error during the dynamic import where we
 * haven't already tried this trick once. We don't pattern-match on
 * the error string because the wording varies across browsers
 * (`Failed to fetch dynamically imported module` in Chrome, `error
 * loading dynamically imported module` in Firefox, the MIME-type
 * variant in some Safari versions).
 */

const RELOAD_FLAG = 'tf:chunk-reloaded';

export function lazyWithRetry(importFn) {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err) {
      const alreadyReloaded = safeRead(RELOAD_FLAG) === '1';
      if (alreadyReloaded) {
        // We already reloaded once and the import is STILL failing —
        // that's a real network/CDN problem, not a stale chunk. Surface
        // it so Sentry / the user sees a real error instead of looping.
        safeRemove(RELOAD_FLAG);
        throw err;
      }
      safeWrite(RELOAD_FLAG, '1');
      // window.location.reload() forces the document to re-fetch the
      // root HTML, which gives the browser the new <script> tags
      // pointing at the new chunks. The user's URL is preserved so
      // they end up exactly where they were trying to go.
      if (typeof window !== 'undefined') {
        // Brief visible signal so the reload doesn't look like the
        // app just blinked for no reason. 200ms is enough to read
        // "Updating…" on most phones without delaying the recovery.
        const banner = document.createElement('div');
        banner.textContent = 'Updating to the latest version…';
        banner.style.cssText = [
          'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:99999',
          'padding:10px 16px', 'background:#0066cc', 'color:#fff',
          'font:14px/1.4 system-ui,-apple-system,sans-serif',
          'text-align:center', 'box-shadow:0 2px 8px rgba(0,0,0,0.2)',
        ].join(';');
        document.body.appendChild(banner);
        setTimeout(() => window.location.reload(), 200);
      }
      // Return a never-resolving promise; the reload takes over before
      // React tries to render anything.
      return new Promise(() => {});
    }
  });
}

/**
 * Clear the reload flag on a successful page load. Call from main.jsx
 * after React mounts — at that point we know the latest chunks loaded
 * fine and we can permit a future reload-retry.
 */
export function markChunkRecoverySuccess() {
  safeRemove(RELOAD_FLAG);
}

function safeRead(key) {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null; }
  catch { return null; }
}
function safeWrite(key, value) {
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value); } catch { /* private mode */ }
}
function safeRemove(key) {
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(key); } catch { /* private mode */ }
}
